/**
 * Sensitivity Analysis Page
 * Analyze how changes in key variables affect returns
 */

import { Card } from '@/components/common/Card';
import { Button } from '@fluentui/react-components';
import { Play24Regular } from '@fluentui/react-icons';
import { mockSensitivityData } from '@/services/mockData';
import { formatPercentage } from '@/utils/formatters';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { CHART_COLORS } from '@/utils/chartColors';

export function Sensitivity() {
  const { variables, results } = mockSensitivityData;

  // Tornado chart data (impact magnitude)
  const tornadoData = results.map(r => ({
    name: r.variable,
    impact: Math.abs(r.impact * 100),
  })).sort((a, b) => b.impact - a.impact);

  return (
    <div className="sensitivity p-6">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-2">Sensitivity Analysis</h1>
          <p className="text-gray-600">Understand how key variables impact project returns</p>
        </div>
        <Button appearance="primary" icon={<Play24Regular />}>
          Run Analysis
        </Button>
      </header>

      {/* Tornado Chart - Impact Ranking */}
      <Card className="p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Variable Impact on IRR</h2>
        <p className="text-sm text-gray-600 mb-4">Ranked by sensitivity (% change in IRR per % change in variable)</p>

        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={tornadoData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" label={{ value: 'Impact (%)', position: 'bottom' }} />
            <YAxis type="category" dataKey="name" width={150} />
            <Tooltip formatter={(value: number) => [`${value.toFixed(1)}%`, 'Impact']} />
            <Legend />
            <Bar dataKey="impact" fill={CHART_COLORS.blue} name="Sensitivity" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Variable Analysis Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {results.map((result, idx) => {
          const variable = variables[idx];
          return (
            <Card key={result.variable} className="p-6">
              <h3 className="font-semibold mb-2">{result.variable}</h3>
              <div className="text-sm text-gray-600 mb-4">
                Impact: <span className="font-semibold text-red-600">{(result.impact * 100).toFixed(1)}%</span> change in IRR per 1% change
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Base Value:</span>
                  <span className="font-semibold">
                    {variable.key.includes('Rate') ? formatPercentage(variable.baseValue) : variable.baseValue.toLocaleString()}
                  </span>
                </div>

                <div className="mt-4">
                  <div className="text-xs text-gray-600 mb-2">Scenario Analysis:</div>
                  {result.values.map((v, i) => (
                    <div key={i} className="flex justify-between py-1">
                      <span className={v.input === variable.baseValue ? 'font-semibold' : ''}>
                        {variable.key.includes('Rate') ? formatPercentage(v.input) : v.input.toLocaleString()}
                      </span>
                      <span className={`${v.input === variable.baseValue ? 'font-bold' : ''} ${
                        v.irr >= 0.18 ? 'text-green-600' : v.irr < 0.15 ? 'text-red-600' : ''
                      }`}>
                        IRR: {formatPercentage(v.irr)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Summary Insights */}
      <Card className="p-6 mt-6">
        <h2 className="text-xl font-semibold mb-4">Key Insights</h2>

        <div className="space-y-3 text-sm">
          <div className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">1.</span>
            <p><strong>Exit Cap Rate</strong> is the most sensitive variable, with a 35% impact on IRR. A 100 bps increase in cap rate reduces IRR by 260 bps.</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">2.</span>
            <p><strong>Construction Costs</strong> have moderate sensitivity (22% impact). Cost overruns of 10% would reduce IRR by approximately 160 bps.</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">3.</span>
            <p><strong>Vacancy Rate</strong> shows lower sensitivity (18% impact), indicating stable tenant base provides downside protection.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
